






























































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {VueMaskDirective} from 'v-mask';
import BindAttrs from '@/utils/binds-attrs';
import {padStart} from 'lodash';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {FieldStyleContext} from '@/fields/field-style-context';

/**
 * General Component for Fields of type DATE
 *
 * https://confluence.ametras.com/x/L4RQAw
 *
 * attachRelatively:
 *  - true: attaches the dialog relatively to the component wrapper
 *          This is needed in case of a position:fixed surrounding panel (usually NOT)
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  directives: {
    VueMaskDirective
  },
  mixins: [BindAttrs]
})
export default class AmetrasDurationFieldTemplate extends Vue {
  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop({
    required: false
  }) value!: string;
  @Prop({
    required: false,
    default: 'HH:mm'
  }) timeFormat!: string;
  @Prop({
    required: false,
    default: 'HH:mm'
  }) isoFormat!: string;
  @Prop({
    required: false,
    default: false
  }) required!: boolean;
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() hideDefaultIcon!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop() readonly!: boolean;
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() styleContext!: FieldStyleContext;
  showPicker = false;
  localeFormattedTextFieldValue = ''; // depending on the locale, e.g. (de) 22:30
  isoFormattedTimepickerValue = ''; // iso formatted timepicker value, e.g. 22:30
  mask = '';
  pickerFormat = 'HH:mm';

  dateRules = [(value: string) => {
    // validates the field value

    if (value && (this.forceShowErrors || !this.readonly)) {
      const isValidDate = this.$moment(value, this.pickerFormat).isValid();
      if (!isValidDate) {
        return 'ungültige Zeitspanne (' + value + ')';
      }
    } else if (!value && this.required && (this.forceShowErrors || !this.readonly)) {
      return 'Zeit fehlt';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  created() {
    if (this.value) {
      // if somebody provided a value from outside
      this.updateInternalModel(this.value);
    }
    // based on the current locale
    // this will create a mask, e.g. xx:xx (representing masked HH:mm)
    this.mask = this.timeFormat.replace(/[hms]/gi, '#');
  }

  @Watch('value') modelChanges(newValue: string, oldValue: string) {
    if (newValue !== oldValue) {
      // if somebody provided a new value from outside
      this.updateInternalModel(newValue);
      this.refreshValidation();
    }
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('isoFormattedTimepickerValue') onTimePicked(newValue: string) {
    if (newValue) {
      // someone picked a value via picker
      const valueAsMoment = this.$moment.duration(newValue);
      if (valueAsMoment.isValid()) {
        this.commitChanges(valueAsMoment.toISOString());
        this.localeFormattedTextFieldValue = padStart(String(valueAsMoment.get('hours')), 2, '0') + ':' +
            padStart(String(valueAsMoment.get('minutes')), 2, '0');
        Vue.nextTick(() => {
          this.refreshValidation();
        });
      }
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  private updateInternalModel(newValue: string) {
    const parsedMomentFromValue = this.$moment.duration(newValue);
    if (parsedMomentFromValue.isValid()) {
      this.localeFormattedTextFieldValue = padStart(String(parsedMomentFromValue.get('hours')), 2, '0') + ':' +
          padStart(String(parsedMomentFromValue.get('minutes')), 2, '0');
      this.isoFormattedTimepickerValue = this.localeFormattedTextFieldValue;
    } else {
      this.localeFormattedTextFieldValue = '';
      this.isoFormattedTimepickerValue = '';
    }
  }

  private updateTimeValue(value: string) {
    if (value) {
      // somebody entered a date
      const valueAsMoment = this.$moment(value, this.pickerFormat);
      if (valueAsMoment.isValid()) {
        const hours = valueAsMoment.hours();
        const minutes = valueAsMoment.minutes();
        const duration = this.$moment.duration().add(hours, 'hours').add(minutes, 'minutes');
        this.commitChanges(duration.toISOString());
      }
    } else {
      this.commitChanges('');
    }
  }

  private commitChanges(newValue: string) {
    // publish the value
    this.$emit('input', newValue);
    this.$emit('change', newValue);
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

}
